import { useEffect, useRef, useState } from "react";
import { INPUT_TEXT, INPUT_TEXT_AREA } from "./GstaInput.constants";
import "./GstaInput.style.scss";

const GstaInput = ({
  label,
  id,
  value,
  setValue,
  isValid,
  placeholder,
  dataTestId,
  Icon,
  errorMessage,
  scrollTarget,
  appearanceClass,
  formatValue,
  type,
  autoFocus,
  clearable,
  debounce = 400,
}) => {
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);

  const [inputValue, setInputValue] = useState(value ?? "");

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const handleChange = (event) => {
    const rawValue = event.target.value;
    const newValue = formatValue ? formatValue(rawValue) : rawValue;

    setInputValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setValue(newValue);
    }, debounce);
  };

  const resetValue = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setInputValue("");
    setValue("");
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (scrollTarget && scrollTarget?.id === id && inputRef.current?.scrollIntoView) {
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [scrollTarget, id]);

  const commonProps = {
    id,
    value: inputValue,
    onChange: handleChange,
    placeholder,
    "data-testid": dataTestId,
    autoFocus,
    autoComplete: "off",
    ref: inputRef,
    onBlur: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setValue(inputValue);
    },
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        setValue(inputValue);
      }
    },
  };

  return (
    <section className={`gsta-input ${appearanceClass ?? ""}`}>
      <div>
        {label && (
          <label htmlFor={id}>
            <b>{label}</b>
          </label>
        )}

        <article>
          {type === INPUT_TEXT && (
            <input
              type="text"
              {...commonProps}
            />
          )}

          {type === INPUT_TEXT_AREA && <textarea {...commonProps} />}

          {clearable && inputValue?.length > 0 ? (
            <i
              className="icon-close"
              onClick={resetValue}
            />
          ) : Icon ? (
            <i
              className={Icon}
              onClick={focusInput}
            />
          ) : null}
        </article>
      </div>

      {errorMessage && <span className="full-width gsta-input-error-message">{!isValid ? errorMessage : " "}</span>}
    </section>
  );
};

export default GstaInput;
